import CheckIcon from '@mui/icons-material/Check';
import LanguageIcon from '@mui/icons-material/Language';
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { pxToRem, pxTovW } from '@geneo2-web/shared-ui';

import { changeAppLanguage } from '../../i18n';
import {
  LanguageCode,
  SUPPORTED_LANGUAGES,
  isSupportedLanguage,
} from '../../i18n/config';

interface LanguageSelectorProps {
  /** Show the native language label even on small screens (useful on login). */
  alwaysShowLabel?: boolean;
}

export default function LanguageSelector({
  alwaysShowLabel = false,
}: LanguageSelectorProps) {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const currentCode: LanguageCode = isSupportedLanguage(i18n.language)
    ? i18n.language
    : 'en';
  const current =
    SUPPORTED_LANGUAGES.find((l) => l.code === currentCode) ??
    SUPPORTED_LANGUAGES[0];

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = async (code: LanguageCode) => {
    handleClose();
    if (code !== currentCode) {
      await changeAppLanguage(code);
    }
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: pxToRem(6),
          px: pxToRem(12),
          py: pxToRem(7),
          borderRadius: pxToRem(10),
          border: '1.5px solid #e2e8f0',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          color: '#475569',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          whiteSpace: 'nowrap',
          '&:hover': {
            borderColor: '#667eea',
            color: '#667eea',
            background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
          },
        }}
      >
        <LanguageIcon sx={{ fontSize: { xs: pxToRem(18), md: pxTovW(22) } }} />
        <Typography
          variant="smallText"
          fontFamily={'poppins'}
          sx={{
            display: alwaysShowLabel
              ? 'block'
              : { xs: 'none', md: 'block' },
          }}
        >
          {current.nativeLabel}
        </Typography>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          mt: pxTovW(10),
          '& .MuiMenu-paper': { borderRadius: pxToRem(10), minWidth: 200 },
        }}
      >
        {SUPPORTED_LANGUAGES.map((lang) => {
          const selected = lang.code === currentCode;
          return (
            <MenuItem
              key={lang.code}
              selected={selected}
              onClick={() => handleSelect(lang.code)}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: pxToRem(16),
                py: pxToRem(10),
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="bodyText" fontWeight="medium">
                  {lang.nativeLabel}
                </Typography>
                {lang.nativeLabel !== lang.label && (
                  <Typography variant="smallText" color="text.secondary">
                    {lang.label}
                  </Typography>
                )}
              </Box>
              {selected && (
                <CheckIcon sx={{ color: '#667eea', fontSize: pxToRem(18) }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
